import type { Distance, Margins, Rectangle } from "@pdfking/math"
import { ZERO_DISTANCE, ZERO_MARGINS } from "@pdfking/math/constants"
import { FoldGrowList } from "@pdfking/templates/fold"

import type { ImpositionObject } from "#internal"

import { CUT_LAYER_ID } from "../../data/LayerList"
import { LANDSCAPE } from "../../data/OrientationKindList"
import { BuildGrowLayout } from "../common/grow/BuildGrowLayout"
import { DrawCutLines } from "../common/grow/DrawCutLines"
import { AddDrawing } from "../common/layout/AddDrawing"

// The flat, unfolded single page: no opening/reverse grow, so its geometry is
// just the page itself and `GrowInnerPages` falls back to one full-canvas cell.
const FLAT_PAGE = FoldGrowList.b

// A pass-through page has no book to lay out, so the dimension context every
// grow expression would read is all zeroes — `b` declares no expressions.
const NO_DIMENSIONS = { depth: ZERO_DISTANCE, sheet_depth: ZERO_DISTANCE }

/**
 * One uploaded page as a real grow layout, so the pass-through preset draws
 * the same cut outline and name block (page number, side, title) an imposed
 * sheet does — rather than a bare media rectangle.
 *
 * `size` is the page's trim box, so the cut lands on it; `padding` is how far
 * the media box overhangs each edge, which is what the bleed guides and the
 * cut lines read. `title` is the job name — the name layer truncates and
 * upper-cases it.
 *
 * Its page draws the uploaded page on the file layer: the trim on the page,
 * the media box through the mask.
 */
export const BuildFilePageLayout = (
  size: Rectangle<Distance>,
  padding: Margins<Distance>,
  title: string | undefined
): ImpositionObject => {
  const object = BuildGrowLayout({
    canvas: size,
    orientation: LANDSCAPE,
    grow: FLAT_PAGE,
    dimensions: NO_DIMENSIONS,
    padding,
    security: ZERO_MARGINS,
    title,
    print_front: true,
    print_rear: false,
    box_opts: undefined,
  })
  return { ...object, layout: AddDrawing(object.layout, CUT_LAYER_ID, DrawCutLines, true) }
}
